// path: frontend/src/pages/TermsOfService.tsx
import { useEffect } from "react";
import { FileText, CheckCircle, XCircle, AlertTriangle, Scale, CreditCard, Ban } from "lucide-react";

export default function TermsOfService() {
    useEffect(() => {
        document.title = "Terms of Service – AutoQuiz AI";
    }, []);

    return (
        <div className="max-w-4xl mx-auto p-6 text-slate-900 dark:text-slate-100">
            {/* Header */}
            <div className="mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 mb-4">
                    <FileText size={24} />
                </div>
                <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
                <p className="text-slate-600 dark:text-slate-400">
                    Last updated: September 12, 2026
                </p>
            </div>

            {/* Introduction */}
            <div className="card p-6 mb-6 bg-purple-50 dark:bg-purple-900/10 border-purple-200 dark:border-purple-900/30">
                <p className="text-sm leading-relaxed text-purple-900 dark:text-purple-200">
                    AutoQuiz AI is operated by MC SANTE PLUS SERVICES LLC. These Terms of
                    Service ("Terms") govern your access to and use of our web application
                    and services. By accessing or using
                    AutoQuiz AI, you agree to be bound by these Terms. If you do not agree to
                    these Terms, please do not use our services.
                </p>
            </div>

            {/* Main Content */}
            <div className="space-y-6">
                {/* Acceptance of Terms */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        Acceptance of Terms
                    </h2>
                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <p>By creating an account or using AutoQuiz AI, you agree to:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Comply with these Terms of Service</li>
                            <li>Follow our Acceptable Use Policy</li>
                            <li>Abide by all applicable laws and regulations</li>
                            <li>Be at least 13 years of age (or 18 in some jurisdictions)</li>
                        </ul>
                        <p className="mt-3">
                            If you are using our services on behalf of an organization, you represent
                            that you have the authority to bind that organization to these Terms.
                        </p>
                    </div>
                </section>

                {/* Educational Purpose */}
                <section className="card p-6 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-amber-900 dark:text-amber-200">
                        <AlertTriangle className="w-5 h-5" />
                        Educational Purpose & Disclaimer
                    </h2>
                    <div className="text-sm space-y-3 text-amber-900 dark:text-amber-200">
                        <p className="font-semibold">
                            AutoQuiz AI is provided for educational and informational purposes only.
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>
                                We are NOT affiliated with any medical licensing board or examination
                                authority, certification body, platform, or third party
                            </li>
                            <li>
                                Content generated is NOT official study material and may contain errors
                            </li>
                            <li>
                                Our service does NOT guarantee exam success or educational outcomes
                            </li>
                            <li>
                                Content should NOT be used as a substitute for professional medical advice
                            </li>
                        </ul>
                        <p className="mt-3">
                            See our <a href="/disclaimer" className="underline hover:no-underline">full disclaimer</a> for more information.
                        </p>
                    </div>
                </section>

                {/* Account Registration */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Account Registration and Security</h2>
                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <div>
                            <h3 className="font-semibold mb-2">Account Creation</h3>
                            <p>To use certain features, you must create an account. You agree to:</p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Provide accurate and complete information</li>
                                <li>Maintain and update your information as needed</li>
                                <li>Keep your password secure and confidential</li>
                                <li>Notify us immediately of unauthorized access</li>
                                <li>Be responsible for all activities under your account</li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-semibold mb-2">Account Termination</h3>
                            <p>
                                You may delete your account at any time. We reserve the right to
                                suspend or terminate accounts that violate these Terms.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Credits and Payment */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        Credits and Payment
                    </h2>
                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <div>
                            <h3 className="font-semibold mb-2">Prepaid Credits</h3>
                            <p>AutoQuiz AI uses prepaid credits instead of recurring subscriptions:</p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li><strong>Signup credits:</strong> New accounts currently receive 10 credits</li>
                                <li><strong>Credit packs:</strong> Purchase 50-credit packs whenever you need more</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Billing</h3>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Credit packs are one-time purchases</li>
                                <li>Credit-pack prices are displayed before purchase and may change over time</li>
                                <li>There are no recurring subscription charges</li>
                                <li>Taxes may apply based on your location</li>
                                <li>Payment processing is handled by Stripe</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Refunds and Failed Renders</h3>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>AutoQuiz AI does not use recurring subscriptions</li>
                                <li>Failed video renders return reserved render credits to your wallet</li>
                                <li>
                                    Credit-pack purchase refunds are handled according to applicable law and our{" "}
                                    <a href="/refund-policy" className="underline hover:no-underline">
                                        Refund & Credit Policy
                                    </a>
                                </li>
                                <li>Refunds may be provided at our discretion for service issues only</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Price Changes</h3>
                            <p>
                                We may change credit-pack prices or credit usage costs. Current prices and credit costs are displayed in the product before purchase or use.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Usage Credits */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Usage Credits and Limits</h2>
                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Quiz generation and previews are free; final video generation currently costs 5 credits</li>
                            <li>Credits are stored in your account wallet</li>
                            <li>Credits do not reset monthly</li>
                            <li>Additional credits may be purchased in one-time 50-credit packs</li>
                            <li>Unused credits remain in your wallet</li>
                            <li>We may change credit prices or usage costs with notice in the product</li>
                        </ul>
                    </div>
                </section>

                {/* Acceptable Use */}
                <section className="card p-6 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                        <Ban className="w-5 h-5" />
                        Acceptable Use Policy
                    </h2>
                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <p className="font-semibold">You agree NOT to:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Use the service for any illegal or unauthorized purpose</li>
                            <li>Violate any laws, regulations, or third-party rights</li>
                            <li>Upload or distribute harmful, offensive, or inappropriate content</li>
                            <li>Attempt to gain unauthorized access to our systems</li>
                            <li>Interfere with or disrupt the service or servers</li>
                            <li>Use automated tools to scrape or harvest data</li>
                            <li>Impersonate others or misrepresent your affiliation</li>
                            <li>Share your account credentials with others</li>
                            <li>Resell or redistribute our services without permission</li>
                            <li>Generate content that infringes copyrights or intellectual property</li>
                            <li>Use the service to provide medical advice or diagnosis</li>
                            <li>Claim affiliation with medical boards or exam authorities</li>
                        </ul>
                        <p className="mt-3 font-semibold">
                            Violation of these terms may result in immediate account termination
                            without notice.
                        </p>
                    </div>
                </section>

                {/* Content and Intellectual Property */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Content and Intellectual Property</h2>
                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <div>
                            <h3 className="font-semibold mb-2">Your Content</h3>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>You retain ownership of content you create using our service</li>
                                <li>You grant us a license to store, process, and display your content</li>
                                <li>You are responsible for ensuring your content doesn't infringe others' rights</li>
                                <li>We may remove content that violates these Terms</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Our Content</h3>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>AutoQuiz AI and its original content are owned by us</li>
                                <li>You may not copy, modify, or distribute our software or materials</li>
                                <li>Our trademarks and branding may not be used without permission</li>
                                <li>Background templates and assets remain our property</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">AI-Generated Content</h3>
                            <p>
                                Content generated by our AI is provided "as-is" without warranties.
                                You are responsible for reviewing and verifying the accuracy of
                                AI-generated content before use.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Disclaimers and Limitations */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        Disclaimers and Limitations of Liability
                    </h2>
                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <div>
                            <h3 className="font-semibold mb-2">Service "AS IS"</h3>
                            <p>
                                AutoQuiz AI is provided "AS IS" and "AS AVAILABLE" without warranties
                                of any kind, either express or implied, including but not limited to:
                            </p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Merchantability or fitness for a particular purpose</li>
                                <li>Accuracy, reliability, or completeness of content</li>
                                <li>Uninterrupted or error-free operation</li>
                                <li>Security or freedom from viruses</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Limitation of Liability</h3>
                            <p className="mb-2">
                                To the maximum extent permitted by law, we shall not be liable for:
                            </p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Indirect, incidental, special, or consequential damages</li>
                                <li>Loss of profits, data, or business opportunities</li>
                                <li>Exam failures or academic outcomes</li>
                                <li>Damages exceeding the amount paid to us in the past 12 months</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Indemnification */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Scale className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        Indemnification
                    </h2>
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                        You agree to indemnify, defend, and hold harmless AutoQuiz AI, its officers,
                        directors, employees, and agents from any claims, damages, losses, or
                        expenses (including legal fees) arising from:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm text-slate-700 dark:text-slate-300">
                        <li>Your use of the service</li>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any third-party rights</li>
                        <li>Content you create or upload</li>
                    </ul>
                </section>

                {/* Changes to Terms */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Changes to Terms</h2>
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                        We may modify these Terms at any time. We will notify users of material
                        changes by email or through the application. Continued use of the service
                        after changes constitutes acceptance of the modified Terms. If you do not
                        agree to the changes, you must stop using the service and may cancel your
                        subscription.
                    </p>
                </section>

                {/* Termination */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Termination</h2>
                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <p>We may terminate or suspend your access immediately, without notice, for:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Violation of these Terms</li>
                            <li>Fraudulent or illegal activity</li>
                            <li>Abuse of the service or other users</li>
                            <li>Non-payment of fees</li>
                            <li>Any lawful reason at our sole discretion</li>
                        </ul>
                        <p className="mt-3">
                            Upon termination, your right to use the service will immediately cease,
                            and we may delete your account and content.
                        </p>
                    </div>
                </section>

                {/* Governing Law */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Governing Law and Disputes</h2>
                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <p>
                            These Terms are governed by the laws of the United States and the state
                            in which our company is registered, without regard to conflict of law
                            principles.
                        </p>
                        <p>
                            Any disputes arising from these Terms or your use of the service shall
                            be resolved through binding arbitration, except where prohibited by law.
                        </p>
                    </div>
                </section>

                {/* Contact */}
                <section className="card p-6 bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-900/30">
                    <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
                    <div className="text-sm text-blue-900 dark:text-blue-200 space-y-2">
                        <p>
                            If you have questions about these Terms of Service, please contact us:
                        </p>
                        <ul className="space-y-1">
                            <li>
                                <strong>Email:</strong>{" "}
                                <a
                                    href="mailto:legal@autoquiz.ai"
                                    className="underline hover:no-underline"
                                >
                                    legal@findtheideas.com
                                </a>
                            </li>
                            <li>
                                <strong>Support:</strong>{" "}
                                <a
                                    href="mailto:services@findtheideas.com"
                                    className="underline hover:no-underline"
                                >
                                    services@findtheideas.com
                                </a>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Acknowledgment */}
                <section className="card p-6 bg-purple-50 dark:bg-purple-900/10 border-purple-200 dark:border-purple-900/30">
                    <h2 className="text-xl font-semibold mb-3 text-purple-900 dark:text-purple-200">
                        Acknowledgment
                    </h2>
                    <p className="text-sm text-purple-800 dark:text-purple-300">
                        BY USING AUTOQUIZ AI, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF
                        SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT
                        AGREE TO THESE TERMS, YOU MUST NOT USE OUR SERVICE.
                    </p>
                </section>
            </div>
        </div>
    );
}